(function () {
  'use strict';

  const engine = window.DDCGiftFinderEngine;
  if (!engine) return;

  function track(name, params) {
    const payload = Object.assign({ event_category: 'gift_finder' }, params || {});
    if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    }
  }

  function money(value) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  }

  function init(root) {
    if (root.dataset.giftFinderInitialized === 'true') return;
    const openButton = root.querySelector('[data-gift-finder-open]');
    const modal = root.querySelector('[data-gift-finder-modal]');
    if (!openButton || !modal) return;
    root.dataset.giftFinderInitialized = 'true';

    const closeButton = modal.querySelector('[data-gift-finder-close]');
    const backButton = modal.querySelector('[data-gift-finder-back]');
    const progress = modal.querySelector('[data-gift-finder-progress]');
    const heading = modal.querySelector('[data-gift-finder-title]');
    const description = modal.querySelector('[data-gift-finder-description]');
    const steps = Array.from(modal.querySelectorAll('[data-gift-finder-step]'));
    const resultsPanel = modal.querySelector('[data-gift-finder-results-panel]');
    const resultsList = modal.querySelector('[data-gift-finder-results]');
    const loading = modal.querySelector('[data-gift-finder-loading]');
    const error = modal.querySelector('[data-gift-finder-error]');
    const restart = modal.querySelector('[data-gift-finder-restart]');
    const whatsapp = modal.querySelector('[data-gift-finder-whatsapp]');
    const catalogUrl = root.dataset.catalogUrl;
    const rootUrl = root.dataset.rootUrl || '/';
    const phone = root.dataset.whatsapp || '573508603080';
    const answers = {};
    let currentStep = 0;
    let productsPromise;
    let lastFocus;
    let requestToken = 0;

    function setStep(index) {
      requestToken += 1;
      currentStep = index;
      steps.forEach((step, i) => { step.hidden = i !== index; });
      resultsPanel.hidden = true;
      progress.textContent = modal.dataset.stepTemplate.replace('CURRENT', String(index + 1)).replace('TOTAL', '3');
      heading.textContent = steps[index].dataset.title;
      description.textContent = steps[index].dataset.description;
      backButton.classList.toggle('is-visible', index > 0);
      const firstChoice = steps[index].querySelector('button');
      if (firstChoice) firstChoice.focus();
    }

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('ddc-gift-finder-open');
      setStep(0);
      closeButton.focus();
      track('gift_finder_open');
    }

    function close() {
      requestToken += 1;
      modal.hidden = true;
      document.body.classList.remove('ddc-gift-finder-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      track('gift_finder_close', { step: currentStep + 1 });
    }

    function fetchProducts() {
      if (!productsPromise) {
        productsPromise = fetch(catalogUrl, { credentials: 'same-origin' })
          .then(response => {
            if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
            return response.json();
          })
          .then(data => Array.isArray(data.products) ? data.products : [])
          .catch(error => {
            productsPromise = null;
            throw error;
          });
      }
      return productsPromise;
    }

    function appendResult(item, position) {
      const product = item.product;
      const link = document.createElement('a');
      const query = 'utm_source=gift_finder&utm_medium=onsite&utm_campaign=guided_selling';
      link.className = 'ddc-gift-finder__result';
      link.href = `${rootUrl}products/${encodeURIComponent(product.handle)}?${query}`.replace('//products', '/products');
      link.dataset.productId = String(product.id);

      const image = document.createElement('img');
      image.loading = 'lazy';
      image.alt = '';
      image.width = 184;
      image.height = 208;
      const source = product.images && product.images[0] ? product.images[0].src : root.dataset.fallbackImage;
      const smallImage = new URL(source, window.location.href);
      const largeImage = new URL(source, window.location.href);
      smallImage.searchParams.set('width', '184');
      largeImage.searchParams.set('width', '368');
      image.src = smallImage.toString();
      image.srcset = `${smallImage} 1x, ${largeImage} 2x`;

      const content = document.createElement('span');
      content.className = 'ddc-gift-finder__result-copy';
      const slot = document.createElement('span');
      slot.className = 'ddc-gift-finder__result-slot';
      slot.textContent = resultsPanel.dataset[`slot${item.slot.charAt(0).toUpperCase()}${item.slot.slice(1)}`] || resultsPanel.dataset.slotAlternative;
      const title = document.createElement('strong');
      title.textContent = product.title;
      const price = document.createElement('span');
      price.className = 'ddc-gift-finder__result-price';
      price.textContent = money(item.variant.price);
      const reason = document.createElement('small');
      reason.className = 'ddc-gift-finder__result-reason';
      reason.textContent = engine.buildReason(item.matches);
      content.append(slot, title, price, reason);
      link.append(image, content);
      link.addEventListener('click', () => track('gift_finder_product_click', {
        product_id: String(product.id),
        product_handle: product.handle,
        position,
      }));
      resultsList.append(link);
    }

    function showResults() {
      const token = ++requestToken;
      steps.forEach(step => { step.hidden = true; });
      resultsPanel.hidden = false;
      progress.textContent = resultsPanel.dataset.progress;
      heading.textContent = resultsPanel.dataset.title;
      description.textContent = resultsPanel.dataset.description;
      backButton.classList.add('is-visible');
      loading.hidden = false;
      error.hidden = true;
      resultsList.setAttribute('aria-busy', 'true');
      resultsList.replaceChildren();
      whatsapp.href = engine.buildWhatsAppUrl(phone, answers);
      track('gift_finder_complete', {
        recipient: answers.recipient,
        occasion: answers.occasion,
        budget: answers.budget,
      });

      fetchProducts().then(products => {
        if (token !== requestToken || modal.hidden || resultsPanel.hidden) return;
        const recommendations = engine.recommendProducts(products, answers, 3);
        loading.hidden = true;
        if (!recommendations.length) throw new Error('No available recommendations');
        recommendations.forEach((item, index) => appendResult(item, index + 1));
        resultsList.setAttribute('aria-busy', 'false');
        track('gift_finder_results_impression', {
          recipient: answers.recipient,
          occasion: answers.occasion,
          budget: answers.budget,
          result_count: recommendations.length,
          product_ids: recommendations.map(item => String(item.product.id)).join(','),
        });
        heading.focus();
      }).catch(() => {
        if (token !== requestToken || modal.hidden || resultsPanel.hidden) return;
        loading.hidden = true;
        error.hidden = false;
        resultsList.setAttribute('aria-busy', 'false');
        track('gift_finder_error');
      });
    }

    steps.forEach((step, index) => {
      step.addEventListener('click', event => {
        const button = event.target.closest('[data-value]');
        if (!button) return;
        const key = step.dataset.giftFinderStep;
        answers[key] = button.dataset.value;
        step.querySelectorAll('[data-value]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
        track('gift_finder_answer', { step: index + 1, question: key, answer: button.dataset.value });
        if (index < steps.length - 1) setStep(index + 1);
        else showResults();
      });
    });

    backButton.addEventListener('click', () => {
      if (!resultsPanel.hidden) setStep(2);
      else if (currentStep > 0) setStep(currentStep - 1);
    });
    restart.addEventListener('click', () => {
      Object.keys(answers).forEach(key => delete answers[key]);
      modal.querySelectorAll('[data-value]').forEach(choice => choice.setAttribute('aria-pressed', 'false'));
      track('gift_finder_restart');
      setStep(0);
    });
    whatsapp.addEventListener('click', () => track('gift_finder_whatsapp', answers));
    openButton.addEventListener('click', open);
    document.querySelectorAll('[data-gift-finder-global-open]').forEach(trigger => {
      if (trigger.dataset.giftFinderBound === 'true') return;
      trigger.dataset.giftFinderBound = 'true';
      trigger.addEventListener('click', event => {
        event.preventDefault();
        open();
      });
    });
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', event => { if (event.target === modal) close(); });
    modal.addEventListener('keydown', event => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll('button:not([hidden]), a[href]:not([hidden])')).filter(el => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  document.querySelectorAll('[data-ddc-gift-finder]').forEach(init);
})();
